<template>
	<div
		v-if="acceptOnlineOrder"
		class="check-wrap"
		:class="{
			'check-wrap--redemption-flow': isTabOpenedByRedemptionEmpty,
			'check-wrap--dark': darkTheme
		}"
	>
		<PageHeader
			route-name="cart"
			color-theme="dark"
			:title="venueInfo.name"
			:is-left-button="isPaymentSuccess || isCartAutoCloseOut"
			:is-close="isPaymentSuccess || isCartAutoCloseOut"
			@close="backToMenu"
			@go-back="backToMenu"
		/>
		<div v-if="isPickUp && !isTabEmpty" class="check-pick-up-time">
			<span class="check-pick-up-time-title">Pickup Order:</span>
			{{ pickupTimeSlot }}
			<template v-if="openedTab.isAsap && closestPickupInterval">
				({{ closestPickupInterval }})
			</template>
		</div>

		<span v-if="sortedCartItems.soldItems.length" class="check-note">
			We updated your order to suggest only available products
		</span>

		<div v-if="!isTabEmpty" class="content">
			<span v-if="customerNameToPrint" class="customer-name">
				{{ customerNameToPrint }}
			</span>
			<div class="check-title">
				<div class="check-title__tab">
					Tab #{{ openedTab.dayOrdinalNumber }}
				</div>
				<div class="check-title__datetime">
					{{ openDateTime }}
				</div>
			</div>

			<div ref="subHeader" class="check-divider sub-header">
				<span>DESCRIPTION</span>
				<span>AMOUNT</span>
			</div>

			<div class="products-holder">
				<ul v-if="sortedCartItems.unSoldItems.length" class="products">
					<li
						v-for="(item, index) in sortedCartItems.unSoldItems"
						:key="`${item.id}${index}`"
					>
						<div class="check-row">
							<truncated-text :text="item.tab.receiptName" />
							<span class="amount">
								<formatted-price
									:price="item.tab.priceWithDiscount * item.quantity"
									decimal-height="70"
								/>
							</span>
						</div>
						<span
							v-if="item.quantity > 1"
							class="check-row-note check-products-price"
						>
							({{ `${item.quantity}@`
							}}<formatted-price
								:price="item.tab.priceWithDiscount"
								decimal-height="70"
							/>)
						</span>

						<div
							v-if="isItemTypeVisible && item.tab && item.tab.employeeName"
							class="item-type"
						>
							<added-by
								:added-by="(item.tab && item.tab.employeeName) || ''"
								is-pickup
							/>
						</div>

						<div
							v-if="item.tab.beverageModName"
							class="check-row modifier-item multiplier"
						>
							{{ item.tab.beverageModName }}
						</div>

						<div
							v-if="item.modifiers && item.modifiers.length"
							class="modifiers"
						>
							<div
								v-for="(modifier, index) in movePriceZeroToEnd(item.modifiers)"
								:key="modifier.id"
								:class="{ 'modifier-item__inline': modifier.price === 0 }"
								class="check-row modifier-item"
							>
								<span v-if="modifier.count && modifier.count > 1">
									{{ modifier.name }}
									<span class="modifier-count">x{{ modifier.count }}</span>
								</span>
								<span v-else>{{ modifier.name }}</span>
								<span
									v-if="
										modifier.price === 0 &&
											index < movePriceZeroToEnd(item.modifiers).length - 1
									"
									>,</span
								>
								<span v-if="modifier.totalPrice > 0" class="amount">
									(<formatted-price
										:price="modifier.totalPrice"
										decimal-height="70"
									/>)
								</span>
							</div>
						</div>

						<template v-if="item.productNotice && !isTabOpenedByRedemption">
							<span class="check-row-note title">SPECIAL INSTRUCTIONS</span>
							<span class="check-row-note">{{ item.productNotice }}</span>
						</template>

						<template
							v-if="
								item.tab.itemizedDiscountPercent ||
									item.tab.itemizedDiscountAmount
							"
						>
							<span class="check-row-note item-discount">
								Employee discount
								<span v-if="item.tab.itemizedDiscountPercent" class="amount">
									({{ item.tab.itemizedDiscountPercent }}%)
								</span>
								<span v-else class="item-discount__price"
									>(<formatted-price
										:price="item.tab.itemizedDiscountAmount || 0"
										decimal-height="70"
									/>)
								</span>
							</span>
						</template>
					</li>
				</ul>
				<template v-if="sortedCartItems.soldItems.length">
					<span class="mark-title">
						<span>SOLD OUT</span>
					</span>
					<ul class="products sold">
						<li
							v-for="(item, index) in sortedCartItems.soldItems"
							:key="`${item.id}${index}`"
						>
							<div class="check-row">
								<span>{{ item.name }}</span>
								<span class="amount">
									<formatted-price
										:price="item.tab.priceWithDiscount * item.quantity"
										decimal-height="70"
									/>
								</span>
							</div>
							<span v-if="item.quantity > 1" class="check-row-note">
								({{ `${item.quantity}@` }}
								<formatted-price
									:price="item.tab.priceWithDiscount"
									decimal-height="70"
								/>
								)
							</span>
							<template
								v-if="
									item.tab.itemizedDiscountPercent ||
										item.tab.itemizedDiscountAmount
								"
							>
								<span class="check-row-note item-discount">
									Employee discount
									<span v-if="item.tab.itemizedDiscountPercent" class="amount">
										({{ item.tab.itemizedDiscountPercent }}%)
									</span>
									<span v-else class="item-discount__price"
										>(<formatted-price
											:price="item.tab.itemizedDiscountAmount || 0"
											decimal-height="70"
										/>)
									</span>
								</span>
							</template>
						</li>
					</ul>
				</template>
			</div>

			<template v-if="includeUtensils">
				<div class="check-divider">
					<span>Utensils</span>
					<span>Included</span>
				</div>
			</template>
			<template v-if="notice">
				<div class="check-notice">
					<span class="check-notice-title">
						ORDER INSTRUCTIONS
					</span>
					<p>{{ notice }}</p>
				</div>
			</template>

			<template v-if="isOrderActionAllowed">
				<div
					class="navigation-buttons d-flex justify-content-center mb-32 mt-16"
				>
					<primary-button
						class="mr-16"
						outlined
						large
						:dark-theme="darkTheme"
						:loading="toCartLoading"
						:disabled="isPaymentReqProcessing"
						@click="goToCart"
					>
						Edit Order
					</primary-button>
					<primary-button
						outlined
						large
						:dark-theme="darkTheme"
						:loading="toProductsLoading"
						:disabled="isPaymentReqProcessing"
						@click="handleGoToProducts"
					>
						Menu
					</primary-button>
				</div>
			</template>

			<span
				v-if="
					!isPaymentSuccess &&
						!isSentToTab &&
						!isSplittedPayment &&
						!isCartAutoCloseOut
				"
				class="cancel"
				:class="{ disabled: isTabClosing || isPaymentReqProcessing }"
				@click="handleCancelOrder"
			>
				Cancel Order
			</span>

			<div class="check-divider check-total">
				<span>Subtotal</span>
				<span class="amount">
					<formatted-price :price="openedTab.checkTotal" decimal-height="60" />
				</span>
			</div>

			<ul class="discounts">
				<li
					v-if="
						venueInfo.smartTabFee &&
							openedTab.amountWithSmartTabFee !== openedTabAmount
					"
					class="check-row"
				>
					<div class="check-row-holder">
						<span>Convenience Fee</span>
						<tooltip />
					</div>
					<span>
						<formatted-price
							:price="venueInfo.smartTabFee"
							decimal-height="70"
						/>
					</span>
				</li>

				<li
					v-for="(discount, index) in finalCheckDiscountsList"
					:key="`final-check-discount-${index}`"
					class="check-row"
				>
					<span>
						{{ discount.finalCheckDiscountName || 'Discount' }}
						<template v-if="discount.finalCheckDiscountInPercent">
							({{ formatDecimalPart(discount.finalCheckDiscountInPercent) }}%)
						</template>
					</span>
					<span>
						-<formatted-price
							:price="discount.finalCheckDiscountAmount || 0"
							decimal-height="70"
						/>
					</span>
				</li>
				<li v-if="openedTab.itemizedDiscountAmount" class="check-row">
					<span>Employee Discount</span>
					<span>
						-<formatted-price
							:price="openedTab.itemizedDiscountAmount || 0"
							decimal-height="70"
						/>
					</span>
				</li>
				<li v-if="openedTab.taxAmount" class="check-row">
					<span>Tax ({{ formatDecimalPart(openedTab.taxPercent) }}%) </span>
					<span>
						<formatted-price :price="openedTab.taxAmount" decimal-height="70" />
					</span>
				</li>
				<li v-if="openedTab.gratuityReceiptAmount" class="check-row">
					<span>
						{{ openedTab.gratuityText }}
						({{ formatDecimalPart(openedTab.gratuityCurrentPercent) }}%)
					</span>
					<span>
						<formatted-price
							:price="openedTab.gratuityReceiptAmount || 0"
							decimal-height="70"
						/>
					</span>
				</li>
				<li v-if="openedTab.surchargeAmount" class="check-row">
					<span v-if="openedTab.surchargePercent">
						{{ openedTab.surchargeText }}
						({{ formatDecimalPart(openedTab.surchargePercent) }}%)
					</span>
					<span v-else>Surcharge</span>
					<span>
						<formatted-price
							:price="openedTab.surchargeAmount"
							decimal-height="70"
						/>
					</span>
				</li>
				<template v-if="customTaxes && customTaxes.length">
					<li
						v-for="customTax in customTaxes"
						:key="customTax.id"
						class="check-row"
					>
						<span>
							{{ customTax.name }}
							({{ formatDecimalPart(customTax.percentage) }}%)
						</span>
						<span>
							<formatted-price :price="customTax.amount" decimal-height="70" />
						</span>
					</li>
				</template>
				<li
					v-if="
						taxSettings.useToGoPackagingFee &&
							openedTab.toGoPackagingFlatFeeAmount
					"
					class="check-row"
				>
					<span v-if="openedTab.toGoPackagingFeeText">
						{{ openedTab.toGoPackagingFeeText }}
					</span>
					<span v-else>Packaging Fee</span>
					<span>
						<formatted-price
							:price="openedTab.toGoPackagingFlatFeeAmount"
							decimal-height="70"
						/>
					</span>
				</li>
				<li
					v-if="
						taxSettings.usePickUpPackagingFee &&
							openedTab.pickUpPackagingFlatFeeAmount
					"
					class="check-row"
				>
					<span v-if="openedTab.pickUpPackagingFeeText">
						{{ openedTab.pickUpPackagingFeeText }}
					</span>
					<span v-else>Packaging Fee</span>
					<span>
						<formatted-price
							:price="openedTab.pickUpPackagingFlatFeeAmount"
							decimal-height="70"
						/>
					</span>
				</li>
			</ul>
			<template v-if="isPaymentSuccess">
				<div v-if="!isTipsRowHidden" class="check-divider tip">
					<span>TIP ({{ Math.round((checkTips / sumForTips) * 100) }}%)</span>
					<span class="amount">
						<formatted-price :price="checkTips" decimal-height="70" />
					</span>
				</div>
			</template>

			<tip-selection
				v-if="isAcceptTipsSectionVisible"
				ref="tipSelection"
				class="tip-selection"
				:class="{ disabled: isTabClosing || isPaymentReqProcessing }"
				:total="sumForTips"
				:tip-percent-values="tipPercentValues"
				:use-tips-percent="useTipsPercent"
				:tip="tip"
				:dark-theme="darkTheme"
				:tips-loading="tipsLoading"
				@apply-tip="handleApplyTip"
				@tips-loading="setTipsLoading"
			/>

			<div class="check-divider check-final">
				<span>TOTAL</span>
				<span class="amount">
					<formatted-price :price="finalTotalPrice" decimal-height="60" />
				</span>
			</div>

			<template v-if="isSplittedPayment && !isPaymentSuccess">
				<div class="total-payment">
					<div>BALANCE DUE</div>
					<span class="amount">
						<formatted-price
							:price="openedTab.balanceDue"
							decimal-height="70"
						/>
					</span>
				</div>
			</template>

			<div v-if="isPickUp" class="check-alt-row">
				<span>Order type</span>
				<span>Pickup</span>
			</div>

			<div
				v-if="
					formattedPhoneNumber &&
						(isEnforceCollectEmailAndPhoneEnabled || isPickUp)
				"
				class="check-alt-row"
			>
				<span>Phone Number</span>
				<span>{{ formattedPhoneNumber }}</span>
			</div>
			<div
				v-if="email && isEnforceCollectEmailAndPhoneEnabled"
				class="check-alt-row"
			>
				<span>Email</span>
				<span>{{ email }}</span>
			</div>

			<div v-if="isPaymentSuccess" class="check-buttons-holder">
				<primary-button class="button" large @click="backToMenu">
					Back to Menu
				</primary-button>
				<primary-button
					class="button"
					outlined
					large
					:dark-theme="darkTheme"
					@click="showCheckoutSuccessModal(true)"
				>
					{{ hadSentAutoEmail ? 'Resend Receipt' : 'Send Receipt' }}
				</primary-button>
			</div>

			<div
				class="message"
				v-if="
					isSplittedPayment &&
						!isPaymentSuccess &&
						!isCartAutoCloseOut &&
						!isCartRecreated
				"
			>
				Your order has been manually adjusted by Staff member and is currently
				being prepared.
				<br />
				Please reach staff member serving your order to submit a payment from
				POS side
			</div>

			<div v-if="isCartAutoCloseOut" class="check-buttons-holder">
				<primary-button
					class="button"
					large
					:dark-theme="darkTheme"
					@click="backToMenu"
				>
					Back to Menu
				</primary-button>
			</div>
		</div>
		<component
			v-else
			:is="isTabOpenedByRedemption ? 'empty-ticketing-tab' : 'empty-tab'"
			:allow-go-to-products="!isTabOpenedByRedemption"
			:customer-name-to-print="customerNameToPrint"
			:ticket-number="openedTab.dayOrdinalNumber"
			:dark-theme="darkTheme"
			@to-products="goToProducts()"
			@close-tab="handleCloseTab"
			@show-empty-current-tab-footer="showEmptyCurrentTabFooter"
		/>
		<template v-if="!isTabEmpty || isTabOpenedByRedemption">
			<check-footer
				v-show="!isPaymentError"
				v-if="!isPaymentSuccess && !isSplittedPayment && !isCartAutoCloseOut"
				ref="checkFooter"
				class="check-footer-initial-animation"
				:class="{
					'initial-footer-translate': !isFooterAnimationComplete,
					'apply-any-animation': isTabOpenedByRedemptionEmpty
				}"
				:is-tab-opened-by-redemption="isTabOpenedByRedemption"
				:is-tab-empty="isTabEmpty"
				:total="openedTabAmount"
				:set-is-payment-req-processing="setIsPaymentReqProcessing"
				:dark-theme="darkTheme"
				@close-empty-tab="handleCloseTab"
				@open-credit-card-modal="openCreditCardModal"
				@cancel-order="handleCancelOrder"
				@tips-loading="setTipsLoading"
				@set-default-tips="setDefaultTips"
				@remove-tips-focus-on-IOS="removeTipsFocusOnIOS"
			/>

			<check-declined-footer
				v-if="isPaymentError && !isSplittedPayment"
				:dark-theme="darkTheme"
				@cancel-order="handleCancelOrder"
				@open-credit-card-modal="openCreditCardModal"
			/>

			<transition name="bottom-slide">
				<credit-card-modal
					v-if="isShowCreditCard"
					:amount="totalPrice"
					:is-pick-up="isPickUp"
					button-text="Save"
					@set-is-pick-up-valid="setIsPickUpValid"
					@close="isShowCreditCard = false"
					@credit-card-added="creditCardAdded"
				/>
			</transition>
		</template>

		<transition name="fade">
			<expired-checkout-modal
				v-if="isExpiredCheckoutModalVisible"
				:dark-theme="darkTheme"
				@close="hideExpiredCheckoutModal"
			/>
			<success-checkout-modal
				v-if="isSuccessModalOpen"
				:dark-theme="darkTheme"
				:cart-id="cartId"
				:is-show-send-receipt="isShowSendReceipt"
				@close="isSuccessModalOpen = false"
			/>
		</transition>
		<transition name="fade">
			<loader v-if="isOrderCanceling" />
		</transition>
		<transition name="fade">
			<close-tab-modal
				v-if="isCloseTabModalOpen || isCancelTabModalOpen"
				:dark-theme="darkTheme"
				:canceling-tab="isCancelTabModalOpen"
				:first-button-action="closeCloseTabModal"
				:second-button-action="onCloseTab"
				:is-loading="isTabClosing"
			/>
		</transition>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
// Components
import PageHeader from '@/components/PageHeader'
import Tooltip from '@/components/Tooltip'
import TruncatedText from '@/components/TruncatedText'
import CheckFooter from './CheckFooter'
import CheckDeclinedFooter from './CheckDeclinedFooter'
import IconSvg from '@/components/IconSvg.vue'
import SuccessCheckoutModal from '@/components/modals/SuccessCheckoutModal'
import ExpiredCheckoutModal from '@/components/modals/ExpiredCheckoutModal'
import CreditCardModal from '@/components/modals/CreditCardModal'
import Loader from '@/components/Loaders/Loader.vue'
import EmptyTab from '@/components/Check/EmptyTab.vue'
import CloseTabModal from '@/components/modals/CloseTabModal.vue'
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
import AddedBy from '@/components/Check/AddedBy.vue'
import TipSelection from '@/components/Check/TipSelection.vue'
import EmptyTicketingTab from '@/components/Check/EmptyTicketingTab.vue'
// Utils
import { getAmPmTimeFromDate, mergeFormattedDateTime } from '@/utils/helpers'
import storage from '@/utils/storage'
import moment from 'moment'
import { groupBy } from 'lodash'
// Mixins
import routerMixin from '@/mixins/routerMixin'

import {
	AMPM_FORMAT,
	CART_STATUS,
	DATE_FORMATS,
	PHONE_NUMBER_REGEX
} from '@/constants'
import FormattedPrice from '@/components/FormattedPrice.vue'

export default {
	name: 'Check',

	mixins: [routerMixin],

	components: {
		FormattedPrice,
		AddedBy,
		CloseTabModal,
		EmptyTab,
		PrimaryButton,
		Loader,
		IconSvg,
		PageHeader,
		TipSelection,
		Tooltip,
		TruncatedText,
		CheckFooter,
		CheckDeclinedFooter,
		SuccessCheckoutModal,
		ExpiredCheckoutModal,
		CreditCardModal,
		EmptyTicketingTab
	},

	data() {
		return {
			isShowCreditCard: false,
			isTabClosing: false,
			isShowSendReceipt: false,
			isPickUpTimeValid: true,
			isExpiredCheckoutModalVisible: false,
			isSuccessModalOpen: false,
			toCartLoading: false,
			toProductsLoading: false,
			isCloseTabModalOpen: false,
			isCancelTabModalOpen: false,
			isOrderCanceling: false,
			isPaymentReqProcessing: false,
			tip: 0,
			tipPercent: 0,
			isFooterAnimationComplete: false,
			// TODO: Move to store after dark/light theme implementation
			darkTheme: true,
			closestPickupInterval: null,
			interval: null,
			tipsLoading: false
		}
	},

	async created() {
		if (!this.acceptOnlineOrder) {
			return this.goToProducts()
		}

		try {
			this.isLoading = true
			await this.getCart()
		} finally {
			this.isLoading = false
		}
	},

	mounted() {
		this.setupStickyObserver()
		if (this.isPaymentSuccess && !this.isReceiptViewed) {
			this.showCheckoutSuccessModal()
			this.resetPayment()
		} else if (this.isCartAutoCloseOut) {
			this.showCheckoutExpiredModal()
			this.resetPayment()
		}
		this.launchInterval()
		this.initTips()
	},

	computed: {
		...mapState('venue', ['venueInfo', 'pickUpTime']),
		...mapState('cart', {
			isSentToTab: 'sentToTab',
			status: 'status',
			cartId: 'id',
			creditCardId: 'creditCardId',
			isInVenue: 'isInVenue',
			isPickUp: 'isPickUp',
			checkTips: 'tips',
			tipsPercent: 'tipsPercent',
			notice: 'notice',
			customerNameToPrint: 'customerNameToPrint',
			hadSentAutoEmail: 'hadSentAutoEmail',
			email: 'email',
			phoneNumber: 'phoneNumber',
			isCartRecreated: 'isCartRecreated',
			location: 'location',
			includeUtensils: 'includeUtensils',
			useTipsPercent: 'useTipsPercent',
			cartTipsPercent: 'tipsPercent'
		}),
		...mapGetters('cart', [
			'openedTab',
			'totalPrice',
			'sumForTips',
			'isTabNotEditable',
			'orderItems',
			'isWaitingPos',
			'isTabEmpty',
			'isTabOpenedByRedemption'
		]),
		...mapGetters('venue', ['venueSettings']),

		isAcceptTipsSectionVisible() {
			return this.acceptTips && !this.isPaymentSuccess && !this.isTabEmpty
		},

		datesList() {
			return Object.keys(this.pickUpTime) || []
		},

		pickupList() {
			const list = []

			this.datesList.forEach(date => {
				const dayList = this.pickUpTime[date].map(item => {
					const startTime = mergeFormattedDateTime(
						date,
						item.startTime,
						this.timeZone
					)
					const endTime = mergeFormattedDateTime(
						date,
						item.endTime,
						this.timeZone
					)

					return { startTime, endTime }
				})
				list.push(...dayList)
			})

			return list
		},

		taxSettings() {
			const { settings } = this.venueInfo
			return settings && settings.taxSettings
		},

		timeZone() {
			return this.venueInfo.timeZone
		},

		pickupTimeSlot() {
			const { pickupTimeFrom, pickupTimeTo } = this.openedTab

			if (pickupTimeFrom && pickupTimeTo) {
				const formattedFrom = this.callGetAmPmTimeFromDate(pickupTimeFrom)
				const formattedTo = this.callGetAmPmTimeFromDate(pickupTimeTo)

				return `${formattedFrom} - ${formattedTo}`
			} else if (this.openedTab.isAsap) {
				return 'ASAP'
			}

			return ''
		},

		customTaxes() {
			return this.openedTab.customTaxes
		},

		isTipsRowHidden() {
			return this.tipsSettings.dontAcceptTips && this.checkTips === 0
		},

		tipsSettings() {
			return this.venueSettings.tipsSettings
		},

		tipPercentValues() {
			const { tip1, tip2, tip3 } = this.tipsSettings
			return [tip1, tip2, tip3]
		},

		acceptTips() {
			return !this.tipsSettings.dontAcceptTips
		},

		acceptOnlineOrder() {
			return this.venueInfo.acceptOnlineOrder
		},

		isEnforceCollectEmailAndPhoneEnabled() {
			return this.venueInfo.settings.isEnforceCollectEmailAndPhoneEnabled
		},

		isSplittedPayment() {
			return this.openedTab.balanceDue !== this.openedTab.amountWithSmartTabFee
		},

		isPaymentSuccess() {
			return (
				this.status === CART_STATUS['PAYMENT_SUCCESS'] || this.isReceiptViewed
			)
		},

		isReceiptViewed() {
			return this.status === CART_STATUS['RECEIPT_VIEWED']
		},

		isPaymentError() {
			return this.status === CART_STATUS['PAYMENT_FAILED']
		},

		isCartAutoCloseOut() {
			return this.status === CART_STATUS['CLOSED_AUTO_CLOSEOUT']
		},

		isPaymentInProgress() {
			return this.status === CART_STATUS['PAYMENT_IN_PROGRESS']
		},

		openDateTime() {
			return moment(this.openedTab.openTime).format(
				DATE_FORMATS.fullWithNamesWithYearAndShortDayNumber
			)
		},

		formattedPhoneNumber() {
			if (this.phoneNumber) {
				const cleanedPhone = this.phoneNumber.toString().replace(/\D/g, '')
				const match = cleanedPhone.match(PHONE_NUMBER_REGEX)

				if (match) {
					return ['(', match[2], ') ', match[3], '-', match[4]].join('')
				}
			}

			return null
		},

		groupedOrderItems() {
			const groupedByHash = groupBy(this.orderItems, 'similarityHash')

			return Object.keys(groupedByHash).map(hash => ({
				...groupedByHash[hash][0],
				quantity: groupedByHash[hash].reduce(
					(sum, item) => sum + item.quantity,
					0
				)
			}))
		},

		sortedCartItems() {
			const unSoldItems = []
			const soldItems = []

			this.groupedOrderItems.forEach(item => {
				if (!item.isEightySixProduct) {
					unSoldItems.push(item)
				} else {
					soldItems.push(item)
				}
			})

			return { unSoldItems, soldItems }
		},

		isOrderActionAllowed() {
			const tabNotOpenedByRedemption = !this.isTabOpenedByRedemption
			const tabEditable = !this.isTabNotEditable
			return (
				tabNotOpenedByRedemption &&
				(tabEditable || this.isPaymentInProgress) &&
				!this.isPaymentSuccess
			)
		},

		openedTabAmount() {
			return this.openedTab.amount
		},

		finalTotalPrice() {
			const tips = this.isPaymentSuccess ? this.checkTips : this.tip
			return parseFloat(this.openedTab.amountWithSmartTabFee) + parseFloat(tips)
		},

		isItemTypeVisible() {
			return !this.isTabOpenedByRedemption
		},

		isTabOpenedByRedemptionEmpty() {
			return Boolean(this.isTabEmpty && this.isTabOpenedByRedemption)
		},

		finalCheckDiscountsList() {
			const tab = this.openedTab
			const discounts =
				tab && Array.isArray(tab.finalCheckDiscounts)
					? tab.finalCheckDiscounts
					: []

			return discounts.filter(function(item) {
				if (!item) return false
				const amount = Number(item.finalCheckDiscountAmount)
				return !isNaN(amount) && amount !== 0
			})
		},

		entireCheckDiscountLabel() {
			const tab = this.openedTab
			const val = tab && tab.entireCheckDiscountInPercent
			if (!val || Number(val) === 0) {
				return ''
			}
			return `(${val}%)`
		}
	},

	watch: {
		isPaymentSuccess() {
			if (
				this.isPaymentSuccess &&
				!this.isTabClosing &&
				!this.isReceiptViewed
			) {
				this.showCheckoutSuccessModal()
				this.resetPayment()
			}
		},
		openedTabAmount() {
			this.initTips()
		},
		checkTips() {
			this.initTips()
		},
		isPaymentError(val) {
			if (val) this.setIsPaymentReqProcessing(false)
		},
		isCartAutoCloseOut(val) {
			if (val) {
				this.showCheckoutExpiredModal()
				this.resetPayment()
			}
		},
		isTabEmpty(newVal) {
			this.handleTabEmptyChange(newVal)
		},
		isTabOpenedByRedemptionEmpty() {
			this.initTips()
		}
	},

	methods: {
		...mapActions('cart', [
			'updateCart',
			'closeTab',
			'getCart',
			'setReceiptShown',
			'cancelOrder'
		]),

		...mapActions('payment', {
			resetPayment: 'resetState'
		}),

		...mapActions('ui', ['addMainError']),

		...mapActions('venue', ['setAllOrderingAvailable']),

		launchInterval() {
			if (this.isPickUp && !this.isTabEmpty) {
				this.closestPickupInterval = this.getFirstPickupInterval()

				this.interval = setInterval(() => {
					this.setAllOrderingAvailable(true)

					this.closestPickupInterval = this.getFirstPickupInterval()
				}, 60000)
			}
		},

		getFirstPickupInterval() {
			const period = this.pickupList[0]
			const formattedFrom = this.callGetAmPmTimeFromDate(period.startTime)
			const formattedTo = this.callGetAmPmTimeFromDate(period.endTime)

			return `${formattedFrom} - ${formattedTo}`
		},

		setIsPaymentReqProcessing(value) {
			this.isPaymentReqProcessing = value
		},

		setTipsLoading(value) {
			this.tipsLoading = value
		},

		setDefaultTips() {
			this.$refs?.tipSelection?.setDefaultTips(true)
		},

		removeTipsFocusOnIOS() {
			this.$refs?.tipSelection?.removeTipsFocusOnIOS()
		},

		handleCancelOrder() {
			this.isCancelTabModalOpen = true
		},

		handleCloseTab() {
			this.isCloseTabModalOpen = true
		},

		closeCloseTabModal() {
			this.isCloseTabModalOpen = false
			this.isCancelTabModalOpen = false
		},

		async handleGoToProducts() {
			this.toProductsLoading = true

			try {
				await this.getCart(true)
			} catch (e) {
				return
			}

			if (this.isCartRecreated) {
				this.toProductsLoading = false

				return
			}

			await this.goToProducts()
		},

		async goToCart() {
			this.toCartLoading = true

			try {
				await this.getCart(true)
			} catch (e) {
				return
			}

			if (this.isCartRecreated) {
				this.toCartLoading = false

				return
			}

			const { query } = this.$route
			await this.$router.push({ name: 'cart', query })
		},

		setIsPickUpValid(val) {
			this.isPickUpTimeValid = val
		},

		callGetAmPmTimeFromDate(dateTime) {
			return getAmPmTimeFromDate(dateTime, this.venueInfo.timeZone, AMPM_FORMAT)
		},

		async onCloseTab() {
			try {
				this.isTabClosing = true

				await this.closeTab({ cartId: this.cartId })
				storage.removeFromStorage('cartId')
				await this.getCart()
			} finally {
				await this.goToProducts()
				this.isTabClosing = false
			}
		},

		showCheckoutSuccessModal(isShowSendReceipt) {
			this.isShowSendReceipt = isShowSendReceipt
			this.isSuccessModalOpen = true
		},

		showCheckoutExpiredModal() {
			this.isExpiredCheckoutModalVisible = true
		},

		hideExpiredCheckoutModal() {
			this.isExpiredCheckoutModalVisible = false
		},

		async backToMenu() {
			await this.setReceiptShown(this.cartId)
			storage.removeFromStorage('cartId')
			await this.goToProducts()
		},

		async openCreditCardModal() {
			try {
				await this.getCart(true)
			} catch (e) {
				console.error(e)
				return
			}

			if (this.isCartRecreated) return

			this.isShowCreditCard = true
		},

		async creditCardAdded(creditCard) {
			await this.getCart(true)

			if (this.isCartRecreated) return

			try {
				await this.updateCart({
					creditCardId: creditCard.id,
					tips: this.checkTips
				})
			} catch (e) {}
		},

		formatDecimalPart(value) {
			try {
				const num = parseFloat(value)
				if (isNaN(num)) {
					return '-'
				}
				const formattedNum = num
					.toString()
					.replace(/(\.\d*?[1-9])0+$/g, '$1')
					.replace(/\.$/, '')
				return formattedNum
			} catch (error) {
				return '-'
			}
		},

		setupStickyObserver() {
			if (this.$refs.subHeader) {
				this.observer = new IntersectionObserver(
					entries => {
						if (!entries || entries.length === 0) {
							return
						}
						const entry = entries[0]
						if (!entry) {
							return
						}
						if (!entry.isIntersecting) {
							this.$refs.subHeader.classList.add('is-sticky')
						} else {
							this.$refs.subHeader.classList.remove('is-sticky')
						}
					},
					{ threshold: [1] }
				)
				this.observer.observe(this.$refs.subHeader)
			}
		},
		movePriceZeroToEnd(arr) {
			const nonZero = arr.filter(item => item.price !== 0)
			const zeros = arr.filter(item => item.price === 0)
			return nonZero.concat(zeros)
		},
		initTips() {
			// TODO Logic duplicated in tip modal
			if (!this.acceptTips) {
				this.tipPercent = 0
				this.tip = 0
			} else if (this.useTipsPercent) {
				this.tipPercent = this.cartTipsPercent
				this.tip = (this.sumForTips * (this.tipPercent / 100)).toFixed(2)
			} else if (this.checkTips !== null && this.checkTips !== undefined) {
				this.tipPercent =
					Math.round((this.checkTips / this.sumForTips) * 100) || 0
				this.tip = this.checkTips
			} else {
				this.tipPercent = this.tipPercentValues[1]
				this.tip = (this.sumForTips * (this.tipPercent / 100)).toFixed(2)
			}
		},
		handleApplyTip(tipData) {
			if (this.$refs.checkFooter && typeof this.$refs.checkFooter.applyTip) {
				this.$refs.checkFooter.applyTip(tipData)
			}
		},
		handleTabEmptyChange(isEmpty) {
			if (!isEmpty) {
				this.$nextTick(() => {
					this.setupStickyObserver()
				})
			} else {
				if (this.observer) {
					this.observer.disconnect()
					this.observer = null
				}
			}
		},
		showEmptyCurrentTabFooter() {
			this.isFooterAnimationComplete = true
		}
	},
	beforeDestroy() {
		if (this.observer) {
			this.observer.disconnect()
		}

		if (this.interval) clearInterval(this.interval)
	}
}
</script>

<style scoped lang="scss">
@import './src/assets/styles/variables';

.check-wrap {
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	background: $white;
	color: $grey-dark;
	max-width: 640px;
	margin: 0 auto;

	&.check-wrap--redemption-flow {
		overflow: hidden;
	}

	&.tips-focused {
		.content {
			overflow-y: initial;
		}
	}

	.animated-logo {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;

		svg {
			width: 42px;
			height: 54px;
		}
	}

	.tooltip-wrap {
		margin: 0 0 0 10px;
	}

	.amount {
		flex-shrink: 0;
		font-size: 16px;
	}

	.credit-card-wrapper {
		width: 100vw;
		left: 50%;
		transform: translateX(-50%);
	}

	.content {
		scrollbar-width: none;
		scroll-behavior: smooth;
	}

	&--dark {
		background-color: $midnight-navy;
		color: $white;

		.sub-header.is-sticky,
		.check-alt-row,
		.check-row,
		.check-notice-title,
		.text-added-by,
		.text-added-by /deep/ .bright {
			color: $white;
		}

		.modifier-item {
			color: $silver-grey;

			&:not(&.modifier-item__inline) {
				justify-content: flex-start;
			}
		}

		.sub-header.is-sticky {
			background-color: $background-box;
		}

		/deep/ .checkout-success-modal {
			.input-field {
				input {
					color: $white !important;
				}
			}
		}
	}
}

.check-pick-up-time {
	padding: 12px;
	font-size: 16px;
	color: $white;
	text-align: center;
	background: $blue-faceless-void;
}

.check-pick-up-time-title {
	font-weight: 700;
}

.check-note {
	display: block;
	padding: 12px 24px;
	color: $white;
	text-align: center;
	background: $natural-red;
}

.tip {
	font-size: 14px;
	padding-top: 0;

	.amount {
		font-size: 14px;
	}
}

.total-payment {
	text-align: center;
	padding: 16px 0;

	.sub-text {
		color: $grey;
		padding: 4px 0;
		font-size: 12px;
	}

	.amount {
		font-size: 24px;
	}
}

.content {
	flex-grow: 1;
	padding: 16px 24px;
	overflow-y: auto;
}

.customer-name {
	display: block;
	padding: 0 0 4px;
	font-weight: 300;
	text-align: left;
	letter-spacing: -0.2px;
}

.check-title {
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-weight: 300;
	padding: 0 0 16px;

	&__tab {
		padding-right: 7px;
	}

	&__datetime {
		padding-left: 7px;
	}
}

.check-divider,
.check-row,
.check-alt-row,
.check-row-holder {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.check-row {
	padding-bottom: 3px;
}

.modifiers {
	padding: 0;
	max-width: calc(100% - 100px);
}

.modifier-item {
	align-items: flex-start;
	padding: 0;
	color: $grey;
	font-size: 14px;

	.amount {
		font-size: 14px;
		margin-left: 15px;
	}

	&__inline {
		display: inline-block;
		margin-right: 5px;
	}
}

.modifier-count {
	text-transform: none;
}

.multiplier {
	font-weight: 700;
}

.item-discount__price {
	margin-left: 5px;
}

.message {
	padding: 30px 50px;
	text-align: center;
	font-size: 16px;
}

.check-alt-row {
	padding: 8px 0;
	font-size: 12px;
	color: $grey;
}

.check-row-note {
	display: block;
	padding: 0;
	font-size: 14px;
	color: $grey;

	&.item-discount {
		display: flex;
		justify-content: flex-start;
		align-items: center;
		color: $semi-dark-blue;
		.amount {
			display: inline-block;
			font-size: 14px;
			margin-left: 5px;
		}
	}

	&.title {
		color: $blue-faceless-void;
	}
}

.check-divider {
	padding: 12px 0;
}

.sub-header {
	position: sticky;
	top: -17px;
	letter-spacing: 0.5px;
	z-index: 2;

	&.is-sticky {
		background-color: $white;
		box-shadow: 0px 1px 6px rgba($black, 0.29);
		margin: 0 -24px;
		padding-left: 24px;
		padding-right: 24px;
	}
}

.mark-title {
	display: block;
	padding: 8px 0;
	font-size: 16px;
	font-weight: 700;
	color: $natural-red;
	text-align: center;

	span {
		display: inline-block;
		padding: 0 18px;
		vertical-align: top;
		position: relative;

		&:before,
		&:after {
			position: absolute;
			left: 0;
			top: 5px;
			width: 6px;
			height: 6px;
			background: $natural-red;
			border-radius: 50%;
			content: '';
		}

		&:after {
			left: auto;
			right: 0;
		}
	}
}

.products + .mark-title {
	padding: 0 0 8px;
}

.products {
	margin: 0;
	padding: 16px 0;
	font-size: 16px;
	list-style: none;

	&.sold {
		margin: 0 -6px;
		padding: 12px 6px;
		border: 1px dashed $natural-red;
		border-radius: 5px;

		.amount {
			font-size: 16px;
			color: $warm-gray;
		}
	}

	li {
		min-height: 35px;
		padding: 0 0 12px;

		&:last-child {
			min-height: 0;
			padding: 0;
		}
	}
}

.check-products-price {
	padding-bottom: 0;
}

.discounts {
	margin: 0;
	padding: 8px 0 15px;
	font-size: 14px;
	color: $dark-blue;
	list-style: none;

	&:empty {
		padding: 0;
	}

	li {
		padding: 0 0 4px;
	}
}

.check-notice {
	padding: 16px 0;
	font-size: 12px;
	color: $grey;
	text-align: center;

	p {
		margin: 0;
		word-break: break-word;
	}
}

.check-notice-title {
	display: block;
	padding: 0 0 10px;
	color: $dark-blue;
}

.check-total,
.check-final {
	font-size: 16px;

	.amount {
		font-size: 24px;
		font-weight: 300;
	}
}

.check-final {
	padding: 20px 0;
	font-size: 24px;
	font-weight: 300;

	.amount {
		font-size: 32px;
	}
}

.navigation-buttons {
	.primary-button {
		width: 288px;
	}
}

.primary-button {
	width: 480px;
}

.cancel {
	display: block;
	width: fit-content;
	margin: 16px auto;
	font-size: 16px;
	color: $semi-dark-blue;
	cursor: pointer;

	&.disabled {
		opacity: 1;
		cursor: default;
	}
}

.check-buttons-holder {
	padding: 24px 0 0;
	text-align: center;
	display: flex;
	align-items: center;

	.button {
		margin: 0 0 0 24px;
		max-width: 300px;

		@media (max-width: 375px) {
			padding-left: 15px;
			padding-right: 15px;
		}
		&:first-child {
			margin: 0;
		}
	}
}

.link {
	text-decoration: underline;
	color: $semi-dark-blue;
	cursor: pointer;
}

.footer {
	font-size: 14px;
	position: relative;
	background: $white;
	box-shadow: 0px -1px 6px 2px rgba($black, 0.16);

	.footer-holder {
		text-align: center;
		padding: 20px;
		font-size: 16px;
	}
}

.tip-selection {
	&.disabled {
		opacity: 0.5;
		pointer-events: none;
	}
}

@media (min-width: 768px) {
	.check-wrap {
		.page-header,
		.content {
			overflow-x: hidden;
		}
	}
}

@media (max-width: 767px) {
	.check-wrap .tooltip-holder {
		width: 210px;
	}

	.check-note {
		padding: 12px;
	}

	.check-buttons-holder {
		padding: 12px 0 0;

		.button {
			margin: 0 0 0 12px;

			&:first-child {
				margin: 0;
			}
		}
	}
}

.dash-line + .dash-line {
	display: none;
}

.check-footer-initial-animation {
	transition: transform 1s, opacity 1s;
}

.apply-any-animation {
	&.initial-footer-translate {
		transform: translateY(200%);
		opacity: 0;
	}

	&.isFooterAnimationComplete {
		transform: translateY(0);
		opacity: 1;
	}
}
</style>
